import {KpisList, type PerformanceDescendantWithId} from '@domain/kpis'
import {HierarchyNodeMetadata} from '@domain/shared'
import {DateRange} from '@hconnect/uikit/src/common'
import {ListTable} from '@hconnect/uikit/src/lib2'
import {useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router'

import {FEATURE_NAMESPACE} from '../consts'
import {ActualVsPlannedRoutes, AggregatedKpiData, Comment, CommentLocationData} from '../types'

import {generateColumns} from './actualVsPlanned.columns'

type MobileLocationCardsProps = {
  data: AggregatedKpiData
  hasQuarterlyData: boolean
  currentTab: KpisList
  dateRange: DateRange<Date>
  setCommentLocationData: (id: CommentLocationData) => void
  comments: Comment[]
  hierarchy?: HierarchyNodeMetadata
  onLocationClick: (item: PerformanceDescendantWithId<AggregatedKpiData>) => void
  getCountryHeaderLink?: ActualVsPlannedRoutes['countryHeader']
}

export const MobileLocationCards = ({
  data,
  hasQuarterlyData,
  currentTab,
  dateRange,
  setCommentLocationData,
  comments,
  hierarchy,
  onLocationClick,
  getCountryHeaderLink
}: MobileLocationCardsProps) => {
  const {t, i18n} = useTranslation(FEATURE_NAMESPACE)
  const navigate = useNavigate()

  const getCountryHeaderClick = useCallback(
    (locationId: string) => {
      const link = getCountryHeaderLink?.(locationId)
      return link ? () => void navigate(link) : undefined
    },
    [getCountryHeaderLink, navigate]
  )

  const columns = useMemo(() => {
    const {historicalData} = data
    return generateColumns({
      translationFn: (key, args = {}) => t(key, args),
      currentTab,
      offsetYearsAmount: historicalData?.length,
      dateRange,
      locale: i18n.language,
      setCommentLocationData,
      comments,
      hierarchy,
      showQuarterlyData: hasQuarterlyData,
      isMobile: true,
      getCountryHeaderClick
    })
  }, [
    t,
    currentTab,
    data,
    dateRange,
    i18n.language,
    setCommentLocationData,
    comments,
    hierarchy,
    hasQuarterlyData,
    getCountryHeaderClick
  ])

  return (
    <ListTable
      data={data.descendants}
      getRowDataTestId={(item) => `ac-vs-pl-table-row-${item.id}`}
      columns={columns}
      onClick={(item) => onLocationClick(item)}
    />
  )
}
